import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton } from '@mui/material';
import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import {
    getCanEditOwnSelectedOrder,
    getCanEditSelectedOrder,
} from 'entities/Order/model/selectors/ordersPermissions';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrderId,
    getSelectedOrderIsPreSubmission,
    getSelectedOrderStatus,
} from 'entities/Order/model/selectors/ordersSelectors';
import {
    getUserCanDeleteOrderItem,
    getUserCanEditPropertyCatalog,
} from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { RemoveItemFromOrderRef } from 'features/RemoveItemFromOrder/ui/RemoveItemFromOrder/RemoveItemFromOrder';
import { getRouteOrdersViewItemDetailed } from 'shared/const/router';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { SuppliersForItem } from 'shared/ui/SuppliersForItem';
import { useOrderItemHooks } from 'widgets/OrderGroupedItems/hooks/OrderItemHooks';

import { OrderGroupedItemsTableRowActions } from './OrderGroupedItemsTableRowActions';

const skeletonSxProps = {
    '&::after': {
        animationDuration: '1.5s',
        background: 'linear-gradient(90deg,transparent,#DCDCDC,transparent)',
    },
    backgroundColor: '#F1F1F1',
    borderRadius: '6px',
};

const baseSupplierRowSxProps = {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0 0 0',
    position: 'relative',
    minHeight: '41px',
};

export const baseSupplierButtonSxProps = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '32px',
    padding: '0 7px',
    boxSizing: 'border-box',
    borderRadius: '4px',
};

export const baseSupplierQtySxProps = {
    width: '200px',
    minWidth: '200px',
    display: 'flex',
    justifyContent: 'right',
    flexGrow: 0,
    paddingRight: '100px',
    fontSize: '14px',
    lineHeight: '18px',
    paddingTop: '7px',
    color: 'rgba(0, 0, 0, 0.87)',
};

export const getBaseSupplierButtonClickableSxProps = (
    canViewProposals: boolean,
) => ({
    cursor: canViewProposals ? 'pointer' : 'default',
    '&:hover': {
        background: canViewProposals ? '#E8EEF6' : 'transparent',
    },
});

const ORDER_ITEM_SELLER_SECTION_DATA_TEST_CLASS = 'order-item-seller-section';

interface OrderGroupItemsTableRowSuppliersProps {
    item: CatalogItem;
    removeItemRef: RefObject<RemoveItemFromOrderRef>;
}

export const OrderGroupItemsTableRowSuppliers = ({
    item,
    removeItemRef,
}: OrderGroupItemsTableRowSuppliersProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedOrderStatus = useSelector(getSelectedOrderStatus);

    const canDeleteOrderItem = useSelector(getUserCanDeleteOrderItem);
    const canEditCatalog = useSelector(getUserCanEditPropertyCatalog);
    const canEditSelectedOrder = useSelector(getCanEditSelectedOrder);
    const canEditOwnSelectedOrder = useSelector(getCanEditOwnSelectedOrder);
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);
    const {
        allSuppliersSubstituted,
        allSuppliersUnavailable,
        someSuppliersSubstituted,
        totalQtyFulfilled,
        totalQtySubstituted,
        totalQtyUnavailable,
        substitutedProposals,
        fulfilledProposals,
    } = useOrderItemHooks(item);

    const hasSubstitutions =
        someSuppliersSubstituted || allSuppliersSubstituted;

    const canDelete =
        (canEditSelectedOrder && canDeleteOrderItem) ||
        !selectedOrderStatus ||
        canEditOwnSelectedOrder;

    const shouldSeeWarnings = !isMaintenanceTechnician && isPreSubmission;

    const canSeeSeller = !(
        isMaintenanceTechnician && selectedOrderStatus === OrderStatuses.DRAFT
    );

    const canViewProposals = !isMaintenanceTechnician || !isPreSubmission;
    const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);

    const areChecksRunning = !item?.isCheckFinished;
    const hasActiveSelectedProposals = item?.selectedProposals?.some(
        (proposal) => proposal.selectedQuantity > 0,
    );

    const hasSomeInactiveSellers = item?.selectedProposals?.some(
        (proposal) =>
            !proposal.isOperationsActivated && proposal.selectedQuantity > 0,
    );

    const openItem = () => {
        if (!canViewProposals || isOrderSubmissionLocked) return;

        navigate(getRouteOrdersViewItemDetailed(selectedOrderId, item.id));
        dispatch(catalogItemActions.setSelectedItem(item));
    };

    return (
        <Box>
            {isPreSubmission ? (
                // Pre-submission container of sellers and delete button
                <Box
                    sx={{
                        ...baseSupplierRowSxProps,
                        justifyContent: 'space-between',
                        position: 'relative',
                    }}
                >
                    {canSeeSeller && (
                        <Box
                            sx={{
                                ...baseSupplierButtonSxProps,
                                ...getBaseSupplierButtonClickableSxProps(
                                    canViewProposals &&
                                        !isOrderSubmissionLocked,
                                ),
                                marginTop: '-7px',
                            }}
                            data-testclass={
                                ORDER_ITEM_SELLER_SECTION_DATA_TEST_CLASS
                            }
                            onClick={openItem}
                        >
                            {areChecksRunning ? (
                                <Skeleton
                                    variant="rounded"
                                    animation="wave"
                                    width="150px"
                                    height="18px"
                                    sx={{
                                        ...skeletonSxProps,
                                    }}
                                />
                            ) : (
                                <>
                                    <SuppliersForItem
                                        selectedProposals={
                                            item.selectedProposals
                                        }
                                        proposals={item.proposals}
                                        showEmptyProposals={
                                            !hasActiveSelectedProposals
                                        }
                                        onViewMoreSuppliers={openItem}
                                    />
                                    {hasSomeInactiveSellers &&
                                        shouldSeeWarnings && (
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: '16px',
                                                    color: 'red',
                                                    marginLeft: '8px',
                                                }}
                                            />
                                        )}
                                </>
                            )}
                        </Box>
                    )}
                    <OrderGroupedItemsTableRowActions
                        itemId={item.id}
                        catalogItemId={item.catalogItemId}
                        itemName={item.title}
                        categoryId={item.categoryId}
                        removeItemRef={removeItemRef}
                        canEditCatalog={canEditCatalog}
                        canDelete={canDelete}
                        isOrderSubmissionLocked={isOrderSubmissionLocked}
                    />
                </Box>
            ) : (
                // Post-submission container of sellers and substitutions
                <Box
                    sx={{
                        paddingBottom: hasSubstitutions ? '19px' : '0',
                        borderBottom: hasSubstitutions
                            ? '1px solid #D9D9D9'
                            : 'none',
                    }}
                >
                    {!hasSubstitutions && (
                        <Box sx={{ ...baseSupplierRowSxProps }}>
                            <Box
                                sx={{
                                    marginTop: '-7px',
                                    ...baseSupplierButtonSxProps,
                                    ...getBaseSupplierButtonClickableSxProps(
                                        canViewProposals,
                                    ),
                                }}
                                data-testclass={
                                    ORDER_ITEM_SELLER_SECTION_DATA_TEST_CLASS
                                }
                                onClick={openItem}
                            >
                                <SuppliersForItem
                                    selectedProposals={item.selectedProposals}
                                    proposals={item.proposals}
                                    opacity={allSuppliersUnavailable ? 0.35 : 1}
                                    subtitle={
                                        totalQtyUnavailable > 0
                                            ? `${totalQtyUnavailable} ${t(
                                                  'unavailable',
                                              )}`
                                            : undefined
                                    }
                                    onViewMoreSuppliers={openItem}
                                />
                            </Box>
                            <OrderGroupedItemsTableRowActions
                                itemId={item.id}
                                catalogItemId={item.catalogItemId}
                                itemName={item.title}
                                categoryId={item.categoryId}
                                removeItemRef={removeItemRef}
                                canEditCatalog={canEditCatalog}
                                canDelete={canDelete}
                                isOrderSubmissionLocked={
                                    isOrderSubmissionLocked
                                }
                            />
                        </Box>
                    )}

                    {allSuppliersSubstituted && (
                        <Box
                            sx={{
                                ...baseSupplierRowSxProps,
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <Box
                                    sx={{
                                        ...baseSupplierButtonSxProps,
                                    }}
                                    data-testclass={
                                        ORDER_ITEM_SELLER_SECTION_DATA_TEST_CLASS
                                    }
                                >
                                    <SuppliersForItem
                                        selectedProposals={
                                            item.selectedProposals
                                        }
                                        proposals={item.proposals}
                                        opacity={0.4}
                                        subtitle={`${totalQtySubstituted} ${t(
                                            'unavailable',
                                        )}`}
                                    />
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    ...baseSupplierQtySxProps,
                                    opacity: 0.4,
                                }}
                            >
                                0
                            </Box>
                        </Box>
                    )}

                    {someSuppliersSubstituted && (
                        <>
                            <Box
                                sx={{
                                    ...baseSupplierRowSxProps,
                                    marginBottom: '11px',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            ...baseSupplierButtonSxProps,
                                            ...getBaseSupplierButtonClickableSxProps(
                                                canViewProposals,
                                            ),
                                        }}
                                        data-testclass={
                                            ORDER_ITEM_SELLER_SECTION_DATA_TEST_CLASS
                                        }
                                        onClick={openItem}
                                    >
                                        <SuppliersForItem
                                            selectedProposals={
                                                fulfilledProposals
                                            }
                                            proposals={item.proposals}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        ...baseSupplierQtySxProps,
                                    }}
                                >
                                    {formatNumber(totalQtyFulfilled)}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    ...baseSupplierRowSxProps,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            ...baseSupplierButtonSxProps,
                                        }}
                                        data-testclass={
                                            ORDER_ITEM_SELLER_SECTION_DATA_TEST_CLASS
                                        }
                                    >
                                        <SuppliersForItem
                                            selectedProposals={
                                                substitutedProposals
                                            }
                                            proposals={item.proposals}
                                            opacity={0.4}
                                            subtitle={`${totalQtySubstituted} ${t(
                                                'unavailable',
                                            )}`}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        ...baseSupplierQtySxProps,
                                        opacity: 0.4,
                                    }}
                                >
                                    0
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};
